import { Button } from '@web-app/components/ui/Button/button';
import { useMedicareBridgeCase } from '@web-app/hooks/medicare-bridge/useMedicareBridgeCase';
import { useMedicareBridgeClinicalPrefill } from '@web-app/hooks/medicare-bridge/useMedicareBridgeClinicalPrefill';
import { trpc } from '@web-app/utils/trpc';
import { useState } from 'react';

import {
  isBridgeCaseOpen,
  isBridgeCaseShown,
  isBridgeReviewUnfinished,
} from './case-visibility';
import { SubmitMedicareBridgeReviewInput } from './medicare-bridge.types';
import { MedicareBridgeBadge } from './MedicareBridgeBadge';
import { MedicareBridgeHandoff } from './MedicareBridgeHandoff';
import { MedicareBridgeLinkedRecords } from './MedicareBridgeLinkedRecords';
import { MedicareBridgeLoadError } from './MedicareBridgeLoadError';
import { MedicareBridgePatientGuide } from './MedicareBridgePatientGuide';
import { MedicareBridgeRequirements } from './MedicareBridgeRequirements';
import { MedicareBridgeReviewForm } from './MedicareBridgeReviewForm';
import { MedicareBridgeReviewSummary } from './MedicareBridgeReviewSummary';

export function MedicareBridgeReviewPanel({
  patientHealthieId,
  appointmentHealthieId,
}: {
  patientHealthieId: string;
  appointmentHealthieId?: string;
}) {
  const query = useMedicareBridgeCase(patientHealthieId, {
    refetchOnMount: 'always',
  });
  const utils = trpc.useUtils();
  const submitReview = trpc.medicareBridge.submitReview.useMutation();
  const [correctingReviewId, setCorrectingReviewId] = useState<string | null>(
    null,
  );
  const bridgeCase = query.data;
  const reviewKey = bridgeCase?.latestReview?.id ?? 'new';
  const correcting = correctingReviewId === reviewKey;
  const caseOpen = isBridgeCaseOpen(bridgeCase);
  const canReview = caseOpen && Boolean(bridgeCase?.reviewSubmissionEnabled);
  const unfinished =
    bridgeCase != null &&
    isBridgeReviewUnfinished(bridgeCase.eligibilityReview);
  const showForm = canReview && (unfinished || correcting);
  const showCorrect = canReview && !unfinished && !correcting;
  const prefill = useMedicareBridgeClinicalPrefill(patientHealthieId, {
    enabled: showForm,
  });

  async function handleSubmit(input: SubmitMedicareBridgeReviewInput) {
    await submitReview.mutateAsync(input);
    await utils.medicareBridge.getCase.invalidate({ patientHealthieId });
  }

  if (query.isLoading) return null;

  if (query.isError && !query.data) {
    return <MedicareBridgeLoadError onRetry={() => void query.refetch()} />;
  }

  if (!bridgeCase || !isBridgeCaseShown(bridgeCase)) return null;

  return (
    <section
      className="mb-6 flex flex-col gap-4 rounded-[16px] border border-border bg-card p-4"
      data-testid="medicare-bridge-review-panel"
    >
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-sm font-semibold text-foreground">
          Medicare Bridge
        </h2>
        <MedicareBridgeBadge bridgeCase={bridgeCase} />
      </div>
      <MedicareBridgeRequirements bridgeCase={bridgeCase} />
      <MedicareBridgeHandoff checklist={bridgeCase.checklist} />
      <MedicareBridgeLinkedRecords bridgeCase={bridgeCase} />
      <MedicareBridgeReviewSummary bridgeCase={bridgeCase} />
      {bridgeCase.status === 'CLOSED' ? (
        <p className="text-sm text-muted-foreground">
          This Bridge case is closed.
        </p>
      ) : null}
      {caseOpen && !bridgeCase.reviewSubmissionEnabled ? (
        <p className="text-sm text-muted-foreground">
          Bridge reviews are not open yet.
        </p>
      ) : null}
      {showCorrect ? (
        <Button
          type="button"
          variant="outline"
          onClick={() => setCorrectingReviewId(reviewKey)}
        >
          Correct review
        </Button>
      ) : null}
      {caseOpen && bridgeCase.eligibilityReview !== 'NOT_ELIGIBLE' ? (
        <MedicareBridgePatientGuide />
      ) : null}
      {showForm ? (
        <MedicareBridgeReviewForm
          key={reviewKey}
          bridgeCase={bridgeCase}
          appointmentHealthieId={appointmentHealthieId}
          clinicalPrefill={prefill.data}
          prefillLoaded={prefill.isSuccess}
          prefillFailed={prefill.isError}
          correcting={correcting}
          onSubmit={handleSubmit}
          onCancel={correcting ? () => setCorrectingReviewId(null) : undefined}
        />
      ) : null}
    </section>
  );
}
